/**
 * Demo site (Try Teamio) — the pure rules behind the /demo link landing page and the /try page. Plan Task 6.3 as
 * reshaped by decision 1a + the SCOPE RULE (2026-10-21), `teamio-serverless-api`
 * docs/plans/2026-10-02-demo-site-plan.md.
 *
 * Only relative imports: cypress/node-tests/demo-landing.test.js executes this exact file via loadEsmUtil.
 * Every storage helper takes the storage object and never throws (private windows and blocked site data).
 */
import { DEMO_PENDING_CODE_KEY, DEMO_ENDED_KEY } from './demoSession';

/** demo-service lib/demoLink.js CODE_RE: 32 random bytes, base64url. */
export const LINK_CODE_RE = /^[A-Za-z0-9_-]{43}$/;

/** The code from a `#t=<code>` fragment (other fragment params allowed), or null. */
export function readLinkCode(hash) {
  if (typeof hash !== 'string') return null;
  const parts = hash.replace(/^#/, '').split('&');
  for (const part of parts) {
    if (part.indexOf('t=') === 0) {
      const code = part.slice(2);
      return LINK_CODE_RE.test(code) ? code : null;
    }
  }
  return null;
}

/** The fragment's code, else the code this tab stashed before a sign-out. The stash is always removed (used once). */
export function initialCode(hash, storage) {
  let pending = null;
  try {
    if (storage) {
      pending = storage.getItem(DEMO_PENDING_CODE_KEY);
      storage.removeItem(DEMO_PENDING_CODE_KEY);
    }
  } catch (e) { pending = null; }
  const fromHash = readLinkCode(hash);
  if (fromHash) return fromHash;
  return typeof pending === 'string' && LINK_CODE_RE.test(pending) ? pending : null;
}

/** Keep the code in this tab while a real practice signs out. → true when stored. */
export function stashPendingCode(storage, code) {
  if (!storage || typeof code !== 'string' || !LINK_CODE_RE.test(code)) return false;
  try {
    storage.setItem(DEMO_PENDING_CODE_KEY, code);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * What the landing page shows after customer/demoLogin fails:
 *   'confirm'     another practice is signed in here (demoLogin refused before any request);
 *   'link_used'   demo-service refused the link (403 link_invalid: used or expired; 400 bad_link);
 *   'unavailable' anything else (503 not set up, 5xx, network, a non-demo token) — worth a retry.
 */
export function outcomeOf(err) {
  if (err && err.code === 'existing_session') return 'confirm';
  const status = err && err.response && err.response.status;
  if (status === 400 || status === 403) return 'link_used';
  return 'unavailable';
}

/** The signed-in practice's name from localStorage `user` (SiteName, then Name), or null. */
export function practiceNameFrom(userJson) {
  let user = null;
  try { user = JSON.parse(userJson); } catch (e) { return null; }
  if (!user || typeof user !== 'object') return null;
  for (const v of [user.SiteName, user.Name]) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/** Did a demo session just end in this tab? Reads and clears the one-shot marker. */
export function takeDemoEnded(storage) {
  try {
    if (!storage) return false;
    const ended = storage.getItem(DEMO_ENDED_KEY) === '1';
    storage.removeItem(DEMO_ENDED_KEY);
    return ended;
  } catch (e) {
    return false;
  }
}

/** window.sessionStorage, or null where reading it throws. */
export function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}
